import React, { useState, useEffect } from 'react';
import './ColorOptimizationTest.css';
import BrowserCompatibilityTest from '../utils/browserCompatibilityTest';
import ColorPerformanceOptimizer from '../utils/colorPerformanceOptimizer';
import CrossBrowserColorTest from '../utils/crossBrowserColorTest';

const ColorOptimizationTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [crossBrowserResults, setCrossBrowserResults] = useState(null);
  const [isRunningTests, setIsRunningTests] = useState(false);

  useEffect(() => {
    // Initialize tests on component mount
    runAllTests();
  }, []);

  const runAllTests = async () => {
    setIsRunningTests(true);
    
    try {
      // Run browser compatibility tests
      const browserTest = new BrowserCompatibilityTest();
      const browserResults = browserTest.getResults();
      setTestResults(browserResults);

      // Get performance metrics
      const performanceOptimizer = new ColorPerformanceOptimizer();
      const metrics = performanceOptimizer.getPerformanceMetrics();
      setPerformanceMetrics(metrics);

      // Run cross-browser tests
      const crossBrowserTest = new CrossBrowserColorTest();
      const crossResults = await crossBrowserTest.runAllTests();
      setCrossBrowserResults(crossResults);

    } catch (error) {
      console.error('Test execution failed:', error);
    } finally {
      setIsRunningTests(false);
    }
  };

  const downloadResults = () => {
    const allResults = {
      browserCompatibility: testResults,
      performanceMetrics: performanceMetrics,
      crossBrowserTests: crossBrowserResults,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(allResults, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `color-optimization-test-results-${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="color-optimization-test">
      <div className="container">
        <h1 className="test-title">Color Optimization & Browser Compatibility Test</h1>
        
        <div className="test-controls">
          <button 
            className="btn-primary test-button"
            onClick={runAllTests}
            disabled={isRunningTests}
          >
            {isRunningTests ? 'Running Tests...' : 'Run All Tests'}
          </button>
          
          {(testResults || performanceMetrics || crossBrowserResults) && (
            <button 
              className="btn-secondary test-button"
              onClick={downloadResults}
            >
              Download Results
            </button>
          )}
        </div>

        {/* Browser Compatibility Results */}
        {testResults && (
          <div className="test-section">
            <h2 className="section-title">Browser Compatibility Results</h2>
            
            <div className="test-grid">
              <div className="test-card">
                <h3>Browser Information</h3>
                <div className="test-details">
                  <p><strong>Browser:</strong> {testResults.browser.isChrome ? 'Chrome' : 
                    testResults.browser.isFirefox ? 'Firefox' : 
                    testResults.browser.isSafari ? 'Safari' : 
                    testResults.browser.isEdge ? 'Edge' : 'Unknown'}</p>
                  <p><strong>Version:</strong> {testResults.browser.version}</p>
                </div>
              </div>

              <div className="test-card">
                <h3>Feature Support</h3>
                <div className="test-details">
                  <div className={`feature-status ${testResults.features.cssCustomProperties ? 'supported' : 'not-supported'}`}>
                    CSS Custom Properties: {testResults.features.cssCustomProperties ? '✓' : '✗'}
                  </div>
                  <div className={`feature-status ${testResults.features.gradientSupport ? 'supported' : 'not-supported'}`}>
                    Gradient Support: {testResults.features.gradientSupport ? '✓' : '✗'}
                  </div>
                  <div className={`feature-status ${testResults.features.backdropFilter ? 'supported' : 'not-supported'}`}>
                    Backdrop Filter: {testResults.features.backdropFilter ? '✓' : '✗'}
                  </div>
                  <div className="feature-status supported">
                    Color Gamut: {testResults.features.colorGamut}
                  </div>
                </div>
              </div>

              <div className="test-card">
                <h3>User Preferences</h3>
                <div className="test-details">
                  <div className={`preference-status ${testResults.userPreferences.prefersReducedMotion ? 'active' : 'inactive'}`}>
                    Reduced Motion: {testResults.userPreferences.prefersReducedMotion ? 'Enabled' : 'Disabled'}
                  </div>
                  <div className={`preference-status ${testResults.userPreferences.prefersHighContrast ? 'active' : 'inactive'}`}>
                    High Contrast: {testResults.userPreferences.prefersHighContrast ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>

              <div className="test-card">
                <h3>Recommendations</h3>
                <div className="test-details">
                  {testResults.report.recommendations.length > 0 ? (
                    testResults.report.recommendations.map((rec, index) => (
                      <div key={index} className={`recommendation ${rec.severity}`}>
                        <strong>{rec.type}:</strong> {rec.message}
                      </div>
                    ))
                  ) : (
                    <div className="recommendation success">
                      All compatibility tests passed! No recommendations needed.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        {performanceMetrics && (
          <div className="test-section">
            <h2 className="section-title">Performance Metrics</h2>
            
            <div className="test-grid">
              <div className="test-card">
                <h3>Frame Rate</h3>
                <div className="metric-value">
                  <span className={`metric-number ${performanceMetrics.frameRate >= 50 ? 'good' : performanceMetrics.frameRate >= 30 ? 'moderate' : 'poor'}`}>
                    {performanceMetrics.frameRate.toFixed(1)} FPS
                  </span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill"
                      style={{ width: `${Math.min(performanceMetrics.frameRate / 60 * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="test-card">
                <h3>Average Frame Time</h3>
                <div className="metric-value">
                  <span className={`metric-number ${performanceMetrics.averageFrameTime <= 16.67 ? 'good' : performanceMetrics.averageFrameTime <= 33.33 ? 'moderate' : 'poor'}`}>
                    {performanceMetrics.averageFrameTime.toFixed(2)} ms
                  </span>
                  <div className="metric-bar">
                    <div 
                      className="metric-fill"
                      style={{ width: `${Math.max(100 - (performanceMetrics.averageFrameTime / 33.33 * 100), 0)}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="test-card">
                <h3>Dropped Frames</h3>
                <div className="metric-value">
                  <span className={`metric-number ${performanceMetrics.droppedFrames === 0 ? 'good' : performanceMetrics.droppedFrames < 10 ? 'moderate' : 'poor'}`}>
                    {performanceMetrics.droppedFrames}
                  </span>
                  <p className="metric-description">
                    Out of {performanceMetrics.totalFrames} total frames
                  </p>
                </div>
              </div>

              <div className="test-card">
                <h3>Active Animations</h3>
                <div className="metric-value">
                  <span className="metric-number good">
                    {performanceMetrics.activeAnimations}
                  </span>
                  <p className="metric-description">
                    Currently running animations
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cross-Browser Test Results */}
        {crossBrowserResults && (
          <div className="test-section">
            <h2 className="section-title">Cross-Browser Test Results</h2>
            
            <div className="test-summary">
              <div className="summary-card">
                <h3>Overall Score</h3>
                <div className={`score ${crossBrowserResults.summary.overallScore >= 80 ? 'good' : crossBrowserResults.summary.overallScore >= 60 ? 'moderate' : 'poor'}`}>
                  {crossBrowserResults.summary.overallScore}%
                </div>
              </div>
              
              <div className="summary-details">
                <div className="summary-item">
                  <span className="summary-label">Color Accuracy:</span>
                  <span className="summary-value">{crossBrowserResults.summary.colorAccuracy}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Gradient Support:</span>
                  <span className="summary-value">{crossBrowserResults.summary.gradientSupport}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Contrast Compliance:</span>
                  <span className="summary-value">{crossBrowserResults.summary.contrastCompliance}</span>
                </div>
              </div>
            </div>

            {crossBrowserResults.recommendations.length > 0 && (
              <div className="recommendations-section">
                <h3>Recommendations</h3>
                {crossBrowserResults.recommendations.map((rec, index) => (
                  <div key={index} className={`recommendation-item ${rec.severity}`}>
                    <div className="recommendation-header">
                      <span className="recommendation-type">{rec.type}</span>
                      <span className={`recommendation-severity ${rec.severity}`}>{rec.severity}</span>
                    </div>
                    <p className="recommendation-message">{rec.message}</p>
                    <p className="recommendation-action"><strong>Action:</strong> {rec.action}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Color Samples for Visual Testing */}
        <div className="test-section">
          <h2 className="section-title">Visual Color Tests</h2>
          
          <div className="color-samples">
            <div className="color-sample-group">
              <h3>Primary Colors</h3>
              <div className="color-grid">
                <div className="color-sample" style={{ backgroundColor: 'var(--color-primary-blue-500, #2563EB)' }}>
                  <span>Primary Blue</span>
                </div>
                <div className="color-sample" style={{ backgroundColor: 'var(--color-primary-purple-500, #7C3AED)' }}>
                  <span>Primary Purple</span>
                </div>
                <div className="color-sample" style={{ backgroundColor: 'var(--color-primary-teal-500, #0891B2)' }}>
                  <span>Primary Teal</span>
                </div>
              </div>
            </div>

            <div className="color-sample-group">
              <h3>Gradient Tests</h3>
              <div className="gradient-samples">
                <div className="gradient-sample gradient-optimized-primary">
                  <span>Primary Gradient</span>
                </div>
                <div className="gradient-sample gradient-optimized-secondary">
                  <span>Secondary Gradient</span>
                </div>
                <div className="gradient-sample gradient-optimized-accent">
                  <span>Accent Gradient</span>
                </div>
              </div>
            </div>

            <div className="color-sample-group">
              <h3>Interactive Elements</h3>
              <div className="interactive-samples">
                <button className="btn-primary hover-optimized">Primary Button</button>
                <button className="btn-secondary hover-optimized">Secondary Button</button>
                <button className="btn-accent hover-optimized">Accent Button</button>
                <button className="btn-success hover-optimized">Success Button</button>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Test Controls */}
        <div className="test-section">
          <h2 className="section-title">Performance Test Controls</h2>
          
          <div className="performance-controls">
            <button 
              className="btn-secondary"
              onClick={() => document.documentElement.classList.toggle('performance-mode-aggressive')}
            >
              Toggle Aggressive Mode
            </button>
            <button 
              className="btn-secondary"
              onClick={() => document.documentElement.classList.toggle('performance-mode-standard')}
            >
              Toggle Standard Mode
            </button>
            <button 
              className="btn-secondary"
              onClick={() => document.documentElement.classList.toggle('performance-mode-enhanced')}
            >
              Toggle Enhanced Mode
            </button>
          </div>
          
          <div className="performance-info">
            <p>Use these controls to test different performance optimization modes:</p>
            <ul>
              <li><strong>Aggressive Mode:</strong> Disables complex animations and gradients for maximum performance</li>
              <li><strong>Standard Mode:</strong> Reduces animation duration and limits concurrent animations</li>
              <li><strong>Enhanced Mode:</strong> Enables all advanced features for high-performance devices</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorOptimizationTest;